/**
 * 节点详情面板
 * 根据节点类型显示不同的编辑表单
 */

import React, { useMemo } from 'react';
import { Descriptions, Empty } from 'antd';
import { CompsForm } from '@ms/shared-components';
import type { FormItemConfig } from '@ms/shared-components/comps/CompsForm';
import type { TreeNode } from '../utils/treeBuilder';

interface NodeDetailPanelProps {
  node: TreeNode;
  onSave?: () => void;
  isModal?: boolean;
  formRef?: any;
}

const NodeDetailPanel: React.FC<NodeDetailPanelProps> = ({
  node,
  onSave,
  isModal = false,
  formRef,
}) => {
  // 根据节点类型获取表单配置（必须在条件返回之前调用 Hook）
  const formItems = useMemo<FormItemConfig[]>(() => {
    if (!node || !node.data) return [];
    switch (node.type) {
      case 'component':
        return [
          {
            name: 'componentId',
            label: '组件ID',
            type: 'input',
            props: { disabled: true },
          },
          {
            name: 'name',
            label: '组件名称',
            type: 'input',
            rules: [{ required: true, message: '请输入组件名称' }],
          },
          {
            name: 'type',
            label: '组件类型',
            type: 'select',
            options: [
              { label: '图表', value: 'chart' },
              { label: '表格', value: 'table' },
              { label: '表单', value: 'form' },
              { label: '仪表盘', value: 'dashboard' },
            ],
          },
          {
            name: 'description',
            label: '描述',
            type: 'textarea',
            props: { rows: 4 },
          },
          {
            name: 'isActive',
            label: '是否启用',
            type: 'switch',
            valuePropName: 'checked',
            props: { checkedChildren: '启用', unCheckedChildren: '禁用' },
          },
        ];
      case 'dataset':
        return [
          {
            name: 'datasetId',
            label: '数据集ID',
            type: 'input',
            props: { disabled: true },
          },
          {
            name: 'name',
            label: '数据集名称',
            type: 'input',
            rules: [{ required: true, message: '请输入数据集名称' }],
          },
          {
            name: 'methodType',
            label: '方法类型',
            type: 'select',
            options: [
              { label: 'Hook', value: 'hook' },
              { label: 'Service', value: 'service' },
              { label: 'Function', value: 'function' },
            ],
          },
          {
            name: 'methodName',
            label: '方法名称',
            type: 'input',
          },
          {
            name: 'description',
            label: '描述',
            type: 'textarea',
            props: { rows: 4 },
          },
        ];
      case 'field':
        return [
          {
            name: 'fieldId',
            label: '字段ID',
            type: 'input',
            props: { disabled: true },
          },
          {
            name: 'fieldName',
            label: '字段名称',
            type: 'input',
            rules: [{ required: true, message: '请输入字段名称' }],
          },
          {
            name: 'fieldType',
            label: '字段类型',
            type: 'input',
            rules: [{ required: true, message: '请输入字段类型' }],
          },
          {
            name: 'description',
            label: '描述',
            type: 'textarea',
            props: { rows: 3 },
          },
        ];
      default:
        return [];
    }
  }, [node?.type]);

  // 获取初始值（必须在条件返回之前调用 Hook）
  const initialValues = useMemo(() => {
    if (!node?.data) return {};
    return { ...node.data };
  }, [node?.data]);

  if (!node || !node.data) {
    return <Empty description="节点数据不存在" />;
  }

  const renderServiceDetail = (data: any) => (
    <Descriptions bordered column={2}>
      <Descriptions.Item label="服务名称" span={2}>
        {data.serviceName}
      </Descriptions.Item>
      <Descriptions.Item label="显示名称">
        {data.displayName}
      </Descriptions.Item>
      <Descriptions.Item label="状态">
        {data.status === 'online' ? '在线' : '离线'}
      </Descriptions.Item>
      <Descriptions.Item label="前端地址" span={2}>
        {data.frontendUrl || '-'}
      </Descriptions.Item>
      <Descriptions.Item label="后端地址" span={2}>
        {data.backendUrl || '-'}
      </Descriptions.Item>
    </Descriptions>
  );

  const renderComponentDetail = (data: any) => (
    <Descriptions bordered column={2}>
      <Descriptions.Item label="组件ID" span={2}>
        {data.componentId}
      </Descriptions.Item>
      <Descriptions.Item label="组件名称">
        {data.name}
      </Descriptions.Item>
      <Descriptions.Item label="组件类型">
        {data.type}
      </Descriptions.Item>
      <Descriptions.Item label="服务名称">
        {data.serviceName}
      </Descriptions.Item>
      <Descriptions.Item label="页面ID">
        {data.pageId}
      </Descriptions.Item>
      <Descriptions.Item label="描述" span={2}>
        {data.description || '-'}
      </Descriptions.Item>
      <Descriptions.Item label="是否启用">
        {data.isActive ? '是' : '否'}
      </Descriptions.Item>
    </Descriptions>
  );

  const renderDatasetDetail = (data: any) => (
    <Descriptions bordered column={2}>
      <Descriptions.Item label="数据集ID" span={2}>
        {data.datasetId}
      </Descriptions.Item>
      <Descriptions.Item label="数据集名称">
        {data.name}
      </Descriptions.Item>
      <Descriptions.Item label="方法类型">
        {data.methodType}
      </Descriptions.Item>
      <Descriptions.Item label="方法名称">
        {data.methodName}
      </Descriptions.Item>
      <Descriptions.Item label="描述" span={2}>
        {data.description || '-'}
      </Descriptions.Item>
    </Descriptions>
  );

  const renderFieldDetail = (data: any) => (
    <Descriptions bordered column={2}>
      <Descriptions.Item label="字段ID" span={2}>
        {data.fieldId}
      </Descriptions.Item>
      <Descriptions.Item label="字段名称">
        {data.fieldName}
      </Descriptions.Item>
      <Descriptions.Item label="字段类型">
        {data.fieldType}
      </Descriptions.Item>
      <Descriptions.Item label="描述" span={2}>
        {data.description || '-'}
      </Descriptions.Item>
    </Descriptions>
  );

  const renderContent = () => {
    // 如果是可编辑的节点类型，显示表单
    if (formItems.length > 0 && formRef) {
      return (
        <CompsForm
          formItems={formItems}
          formRef={formRef}
          initialValues={initialValues}
          layout="horizontal"
          contentPadding={0}
        />
      );
    }

    // 否则显示只读详情
    switch (node.type) {
      case 'service':
        return renderServiceDetail(node.data);
      case 'component':
        return renderComponentDetail(node.data);
      case 'dataset':
        return renderDatasetDetail(node.data);
      case 'field':
        return renderFieldDetail(node.data);
      case 'page':
        return (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="服务名称">
              {node.data.serviceName}
            </Descriptions.Item>
            <Descriptions.Item label="页面ID">
              {node.data.pageId}
            </Descriptions.Item>
          </Descriptions>
        );
      default:
        return <Empty description="未知节点类型" />;
    }
  };

  return <div>{renderContent()}</div>;
};

export default NodeDetailPanel;
